import React, { useContext, useState } from "react";

import CustomModal from "../../../partials/CustomModal";
import ReviewDatasetCard from "./ReviewDatasetCard";

import worldMapSvg from "../../../../../images/worldMap.svg";
import WindIcon from "../../../Icons/WindIcon";
import DatasetConfigurationIcon from "../../../Icons/DatasetConfigurationIcon";
import datasetLogo from "../../../../../images/datasetLogo.svg";
import ServicesApi from "../../../../api/services";
import {
  useProjectDescriptionModalStyles,
  ProjectDescriptionModalPropTypes,
} from "./useStyles";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { LocationSelectContext } from "../../../context/LocationSelectPageContext";
import { SnackbarContext } from "../../../context/SnackbarContext";
import { RegionContext } from "../../../context/RegionContext";
import { apiDataSource, CORDEX, HAD_UK, LOCA } from "../datasets";
import { allMatrixData } from "../WindRelatedVariablesModal/data";

const ProjectDescriptionModal = (props: ProjectDescriptionModalPropTypes) => {
  const classes = useProjectDescriptionModalStyles();

  const { open, closeModalHandler } = props;
  const { geoJSON } = useContext(RegionContext);
  //eslint-disable-next-line
  const [geoJson, setGeoJson] = geoJSON;

  const {
    snackbarState: { setSnackbar },
  } = useContext(SnackbarContext);

  const {
    selectedClimateVariableState,
    selectedDatasetState,
    selectedResolutionState,
    retrivableData,
    climateDataHistoryModalState,
    handlers,
    datasetSelectCardVisibleState,
  } = useContext(LocationSelectContext);

  const { setIsDatasetSelectCardVisible } = datasetSelectCardVisibleState;

  const [retriveData] = retrivableData;

  const { selectedDataset } = selectedDatasetState;

  const { selectedClimateVariable } = selectedClimateVariableState;

  const { setIsClimateDataHistoryModalOpen } = climateDataHistoryModalState;

  const { selectedResolution } = selectedResolutionState;

  const [projectName, setProjectName] = useState("");

  const [jobCode, setJobCode] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [isJobCodeValid, setIsJobCodeValid] = useState(false);

  const { closeAllModalsAndPopups } = handlers;

  const data: Array<{
    title: string;
    description: string | React.ReactNode;
    icon?: React.ReactNode;
    iconPath?: string;
  }> = [
    {
      title: "Dataset:",
      description: selectedDataset,
      iconPath: datasetLogo,
    },
    {
      title: "Location:",
      description: `${retriveData.region}, ${retriveData.country}`,
      iconPath: worldMapSvg,
    },
    {
      title: "Climate Variable:",
      description: selectedClimateVariable,
      icon: (
        <WindIcon
          style={{
            color: "white",

            fontSize: "2.5rem",
          }}
        />
      ),
    },

    {
      title: "Dataset Configuration:",
      description: (
        <span>
          Historical Data {retriveData.projection}
          <br />
          {selectedDataset !== HAD_UK &&
            `Projected Data ${retriveData.projectedData}`}
        </span>
      ),
      icon: (
        <DatasetConfigurationIcon
          style={{
            color: "white",

            fontSize: "2.5rem",
          }}
        />
      ),
    },
  ];

  const jobCodeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filteredValue = event.target.value
      .replace(/[^0-9-]/g, "")
      .replace(/^(\d{6})(\d{1,2})/, "$1-$2");

    if (filteredValue.length > 9) return;

    setIsJobCodeValid(false);

    setJobCode(filteredValue);
  };

  const shouldRetriveDataButtonEnabled = () => {
    if (projectName === "" || jobCode === "" || isLoading) {
      return true;
    }
    return false;
  };

  const onRetriveData = () => {
    if (selectedDataset !== CORDEX) {
      setSnackbar({
        open: true,
        color: "warning",
        message: "Warning: Please be aware that the outputs have not been QA'd",
      });
    }

    const regex = /[0-9]{2,2}-[0-9]/gm;

    const isValid = regex.test(jobCode);

    if (!isValid) {
      setIsJobCodeValid(true);
      return;
    } else {
      setIsJobCodeValid(false);
    }

    let latSearch: Array<number> = [];
    let lonSearch: Array<number> = [];
    //eslint-disable-next-line
    const getAllLatLon = geoJson.features.map((item, index) => {
      if (index > 0) {
        latSearch.push(+geoJson.features[index].geometry.coordinates[0]);
        lonSearch.push(+geoJson.features[index].geometry.coordinates[1]);
      }
    });
    let datasourceVal;
    if (selectedDataset === "UKCP18 Probabilistic (Average Trends)") {
      datasourceVal = apiDataSource.UKCP18_PROB;
    } else if (selectedDataset === "UKCP18 High-Res (Extreme Indicators)") {
      datasourceVal = apiDataSource.UKCP18_HIGH_RES;
    } else if (selectedDataset === HAD_UK) {
      datasourceVal = apiDataSource.HAD_UK;
    }

    const commonData = {
      region: {
        name: retriveData.region,

        lat: latSearch,
        lon: lonSearch,
      },
      country: retriveData.country,

      threshold_value_less_than:
        retriveData.thresholdValueLessThan === null
          ? null
          : Number(retriveData.thresholdValueLessThan),
      threshold_value_more_than:
        retriveData.thresholdValueMoreThan === null
          ? null
          : Number(retriveData.thresholdValueMoreThan),
      main_variable: retriveData.climateVariable,
      // datasource: retriveData.dataset,
      project_name: projectName,
    };

    const payloadForCordex = {
      ...commonData,
      job_code: jobCode,
      variable: [retriveData.climateVariableSource],
      primary_key: 12,
      historical_from_year: parseInt(retriveData.historical_from_year),
      historical_to_year: parseInt(retriveData.historical_to_year),
      projectional_from_year: parseInt(retriveData.projectional_from_year),
      projectional_to_year: parseInt(retriveData.projectional_to_year),
      datasource: retriveData.dataset,
    };

    const payloadForLOCA = {
      ...commonData,
      project_code: jobCode,
      variable_long_name: [allMatrixData[retriveData.climateVariableSource]],
      consumer_id: JSON.parse(localStorage.getItem("KLIIMA_PROFILE")!)?.name,
      primary_key: 12,
      datasource: apiDataSource.LOCA,
      historical_from_date: `${retriveData.historical_from_year}-01-01`,
      historical_to_date: `${retriveData.historical_to_year}-12-31`,
      projectional_from_date: `${retriveData.projectional_from_year}-01-01`,
      projectional_to_date: `${retriveData.projectional_to_year}-12-31`,
    };

    const payloadForUKCP = {
      ...commonData,
      project_code: jobCode,
      resolution: selectedResolution,
      consumer_id: JSON.parse(localStorage.getItem("KLIIMA_PROFILE")!)?.name,
      variable_long_name: [allMatrixData[retriveData.climateVariableSource]],
      historical_from_date: `${retriveData.historical_from_year-1}-12-01`,
      historical_to_date: `${retriveData.historical_to_year}-11-30`,
      projectional_from_date: `${retriveData.projectional_from_year-1}-12-01`,
      projectional_to_date: `${retriveData.projectional_to_year}-11-30`,
      datasource: datasourceVal,
    };
    const payloadForHADUK = {
      ...commonData,
      project_code: jobCode,
      resolution: selectedResolution,
      consumer_id: JSON.parse(localStorage.getItem("KLIIMA_PROFILE")!)?.name,
      variable_long_name: [allMatrixData[retriveData.climateVariableSource]],
      historical_from_date: `${retriveData.historical_from_year-1}-12-01`,
      historical_to_date: `${retriveData.historical_to_year}-11-30`,
      projectional_from_date: `${retriveData.projectional_from_year-1}-12-01`,
      projectional_to_date: `${retriveData.projectional_to_year}-11-30`,
      datasource: apiDataSource.HAD_UK,
    };
    let payload;

    if (selectedDataset?.includes("UKCP")) {
      payload = payloadForUKCP;
    } else if (selectedDataset?.includes("LOCA")) {
      payload = payloadForLOCA;
    } else {
      payload = payloadForCordex;
    }

    const sendRetreveData = async () => {
      setIsLoading(true);

      const { data } = await ServicesApi.post<[]>(
        "submit-request",
        selectedDataset === HAD_UK ? payloadForHADUK : payload
      );

      return data;
    };
    sendRetreveData()
      .catch(() => setIsLoading(false))
      .then((data) => {
        setIsLoading(false);
        if (data !== undefined) {
          setSnackbar({
            color: "success",
            open: true,
            message: 'Task added successfully wait for email response!!!"',
          });
          closeAllModalsAndPopups();
          setIsDatasetSelectCardVisible(false);
          setIsClimateDataHistoryModalOpen(true);
        } else {
          setSnackbar({
            color: "error",
            open: true,
            message:
              "Something went wrong, please contact KliimaSupport@arup.com",
          });
        }
      });
  };

  const reviewDatasetCards = data.map((item, i) => (
    <div key={i} style={{ width: 200 }}>
      <ReviewDatasetCard
        title={item.title}
        description={item.description}
        icon={item.icon}
        iconPath={item.iconPath}
      />
    </div>
  ));

  return (
    <CustomModal
      title="Review dataset selections..."
      headingFontSize="1.5rem"
      isOpen={open}
      onClick={closeModalHandler}
    >
      <div className={classes.iconsContainer}>{reviewDatasetCards}</div>

      <div className={classes.contentContainer}>
        <Typography>
          Please enter a title for this item of data retrieval. This will be
          added to your prohect history page enabling you to find and manage the
          data you have requested.
        </Typography>

        <div className={classes.textFieldContainer}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Request Title"
            className={classes.textField}
            inputProps={{
              style: {
                height: "32px",
                padding: "0 14px",
              },
            }}
            onChange={(e) => setProjectName(e.target.value)}
            value={projectName}
          />

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter Job Number"
            className={classes.textField}
            error={isJobCodeValid}
            inputProps={{
              style: {
                height: "32px",
                padding: "0 14px",
              },
            }}
            style={{ marginTop: "17px", marginBottom: "17px" }}
            onChange={jobCodeHandler}
            value={jobCode}
          />
        </div>

        <Typography>
          Please enter the Job Number for the project or proposal on which you
          are using Kliima. This is for our records on where and how in Arup the
          tool is being used. There is currently no direct charge made to your
          Job Number but we expect your clients to be charged appropriately for
          the value that Kliima provides. Please contact the team
          <a
            href="https://arup.sharepoint.com/sites/Kliima/SitePages/Kliima-Team.aspx"
            target="_blank"
            rel="noreferrer"
          >
            https://arup.sharepoint.com/sites/Kliima/SitePages/Kliima-Team.aspx
          </a>
          if you need guidance on pricing.
          <br />
          <br />
          <em>
            <b>
              Please note we will start charging for use of the tool in{" "}
              {new Date().getFullYear()} to fund ongoing development and
              maintenance.
            </b>
          </em>
          <br />
          <br />
          Once you have checked and are happy with the selections you have made,
          click ‘Retrieve Data’ to proceed. Depending on the nature of your
          request, this can take up to several hours to process. Next, you will
          be taken to your project history page which allows you to manage the
          data you have requested and view the progress and status of previous
          requests.
          <br />
          <br />
          Please fill out this
          <a
            href="https://forms.office.com/pages/responsepage.aspx?id=QYvkSjcBmUWGYfxkH-d76rfu0Sqwi9VLraQ_SMDDWZpUNUg0RzdRNFgxV0FGVE9QNFJJNU5CME4yMi4u_"
            target="_blank"
            rel="noreferrer"
          >
            feedback form
          </a>
        </Typography>
      </div>

      <Grid item container justify="center" style={{ marginTop: "1.5rem" }}>
        <Button
          onClick={() => {
            onRetriveData();
          }}
          className={classes.btn}
          variant="contained"
          disableElevation
          disabled={shouldRetriveDataButtonEnabled()}
        >
          {isLoading ? (
            <CircularProgress color="primary" size={20} />
          ) : (
            "Retrieve Data"
          )}
        </Button>
      </Grid>
    </CustomModal>
  );
};

export default ProjectDescriptionModal;
